import { useEffect, type CSSProperties } from "react";
import { Link, useSearchParams } from "react-router";
import { brandColors as colors } from "@/lib/palette";
import { fonts } from "@/lib/fonts";
import Footer from "@/components/ui/Footer";

export function ShopSuccess() {
  // clear cart on mount
  useEffect(() => {
    try {
      localStorage.removeItem("aflt-cart");
    } catch {
      /* noop */
    }
  }, []);
  return (
    <Result
      title="Thanks for your order!"
      body="A confirmation is on its way to your inbox. We'll send tracking info as soon as it ships."
      cta={{ to: "/shop", label: "Back to shop" }}
    />
  );
}

export function ShopCancel() {
  return (
    <Result
      title="Checkout cancelled"
      body="No charges were made. Your cart is still here when you're ready."
      cta={{ to: "/shop", label: "Back to shop" }}
    />
  );
}

export function RetreatSuccess() {
  const [params] = useSearchParams();
  const isBalance = params.get("balance") === "1";
  return (
    <div className="retreat-success-page">
      <style>{retreatSuccessCss}</style>
      <ConfettiBurst />
      <main className="retreat-success-main">
        <p className="success-eyebrow">Retreat registration</p>
        <h1>{isBalance ? "Balance received!" : "You're in!"}</h1>
        <p className="success-copy">
          {isBalance
            ? "Thanks for taking care of your retreat balance. Your registration is fully paid."
            : "Thanks for signing up for the retreat. Your spot is saved, and a confirmation is on its way to your inbox."}
        </p>
        <div className="success-actions">
          <Link to="/retreats" className="success-primary">
            View retreats
          </Link>
          <Link to="/" className="success-secondary">
            Back home
          </Link>
        </div>
      </main>
      <Footer />
    </div>
  );
}

export function RetreatCancel() {
  return (
    <Result
      title="Checkout cancelled"
      body="No charges were made. Pop back any time to reserve your spot."
      cta={{ to: "/retreats", label: "All retreats" }}
    />
  );
}

function ConfettiBurst() {
  const pieces = Array.from({ length: 42 }, (_, index) => ({
    id: index,
    left: `${(index * 17) % 100}%`,
    delay: `${(index % 9) * 0.11}s`,
    duration: `${2.5 + (index % 7) * 0.18}s`,
    color: [colors.berryRose, colors.tan, colors.forestGreen, colors.softLilac, colors.berryPink][index % 5],
    rotate: `${(index * 37) % 180}deg`,
    drift: `${((index % 7) - 3) * 18}px`,
  }));

  return (
    <div className="confetti-layer" aria-hidden="true">
      {pieces.map((piece) => (
        <span
          key={piece.id}
          className="confetti-piece"
          style={{
            left: piece.left,
            animationDelay: piece.delay,
            animationDuration: piece.duration,
            backgroundColor: piece.color,
            transform: `rotate(${piece.rotate})`,
            "--drift": piece.drift,
          } as CSSProperties}
        />
      ))}
    </div>
  );
}

function Result({
  title,
  body,
  cta,
}: {
  title: string;
  body: string;
  cta: { to: string; label: string };
}) {
  return (
    <div className="min-h-dvh" style={{ backgroundColor: colors.cream }}>
      <main className="pt-32 pb-16 px-6 max-w-2xl mx-auto text-center">
        <h1
          className="text-4xl mb-4"
          style={{ fontFamily: fonts.logo, color: colors.navy }}
        >
          {title}
        </h1>
        <p
          className="text-base mb-8"
          style={{ fontFamily: fonts.body, color: `${colors.navy}cc` }}
        >
          {body}
        </p>
        <Link
          to={cta.to}
          className="inline-block px-8 py-3 rounded-full text-white"
          style={{
            backgroundColor: colors.berryRose,
            fontFamily: fonts.body,
            fontWeight: 700,
            letterSpacing: "0.05em",
            textTransform: "uppercase",
            fontSize: 13,
          }}
        >
          {cta.label}
        </Link>
      </main>
      <Footer />
    </div>
  );
}

const retreatSuccessCss = `
  .retreat-success-page {
    min-height: 100vh;
    position: relative;
    overflow: hidden;
    background:
      radial-gradient(circle at 20% 15%, rgba(212,96,106,0.16), transparent 28%),
      radial-gradient(circle at 82% 20%, rgba(201,147,76,0.18), transparent 30%),
      ${colors.cream};
  }
  .retreat-success-main {
    min-height: calc(100vh - 220px);
    width: min(760px, calc(100% - 32px));
    margin: 0 auto;
    padding: 132px 0 88px;
    position: relative;
    z-index: 1;
    text-align: center;
  }
  .success-eyebrow {
    margin: 0 0 10px;
    font-family: ${fonts.body};
    font-size: 12px;
    font-weight: 800;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: ${colors.berryRose};
  }
  .retreat-success-main h1 {
    margin: 0;
    font-family: ${fonts.logo};
    font-size: clamp(52px, 9vw, 96px);
    font-weight: 500;
    line-height: 0.95;
    color: ${colors.navy};
  }
  .success-copy {
    max-width: 560px;
    margin: 22px auto 0;
    font-family: ${fonts.body};
    font-size: clamp(16px, 2.4vw, 19px);
    line-height: 1.7;
    color: rgba(8,45,61,0.78);
  }
  .success-actions {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 12px;
    margin-top: 34px;
  }
  .success-primary,
  .success-secondary {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 44px;
    padding: 0 22px;
    border-radius: 999px;
    font-family: ${fonts.body};
    font-size: 13px;
    font-weight: 800;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    text-decoration: none;
  }
  .success-primary {
    background: ${colors.berryRose};
    color: #fff;
    box-shadow: 0 12px 26px -18px rgba(212,96,106,0.8);
  }
  .success-secondary {
    background: #fff;
    border: 1px solid rgba(201,147,76,0.48);
    color: ${colors.navy};
  }
  .confetti-layer {
    position: fixed;
    inset: 0;
    pointer-events: none;
    z-index: 0;
    overflow: hidden;
  }
  .confetti-piece {
    position: absolute;
    top: -24px;
    width: 9px;
    height: 16px;
    border-radius: 2px;
    opacity: 0.9;
    animation-name: confettiFall;
    animation-timing-function: cubic-bezier(.32,.02,.6,1);
    animation-iteration-count: infinite;
  }
  @keyframes confettiFall {
    0% { translate: 0 -10vh; rotate: 0deg; opacity: 0; }
    12% { opacity: 0.95; }
    100% { translate: var(--drift, 24px) 115vh; rotate: 540deg; opacity: 0; }
  }
  @media (prefers-reduced-motion: reduce) {
    .confetti-piece {
      animation: none;
    }
  }
`;
